/**
 * The router: forty lines, because there are eight routes.
 *
 * A dependency would be the ordinary answer and is the wrong one here. What this
 * application needs from a router is the current path, a way to change it without
 * a reload, and the back button working — and that is `history.pushState`, a
 * `popstate` listener and a `useSyncExternalStore`. A library brings a component
 * model, a data-loading opinion and a migration guide, none of which this needs.
 *
 * The one thing it does carefully is intercept clicks on internal links, so an
 * ordinary `<a href>` navigates without a reload. That keeps every link in the
 * application a real link — right-clickable, openable in a new tab, visible in the
 * status bar — which a button pretending to be one is not.
 */
import { useSyncExternalStore } from "react";

const listeners = new Set<() => void>();

function announce(): void {
  for (const l of listeners) l();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => void listeners.delete(fn);
}

function snapshot(): string {
  return window.location.pathname + window.location.search;
}

window.addEventListener("popstate", announce);

/** The current path, as a hook. */
export function usePath(): string {
  return useSyncExternalStore(subscribe, snapshot, () => "/");
}

export function go(to: string, opts: { replace?: boolean } = {}): void {
  if (to === snapshot()) return;
  if (opts.replace) window.history.replaceState(null, "", to);
  else window.history.pushState(null, "", to);
  // Back to the top on a route change, which a real page load would have done and
  // which is otherwise the most disorienting thing about a single-page
  // application: following a link and arriving half way down the next screen.
  window.scrollTo({ top: 0 });
  announce();
}

/**
 * Makes ordinary links navigate.
 *
 * Installed once. It deliberately declines a click that the user has made into a
 * request for something else — a modified click, a middle click, an anchor with a
 * target, an external address, a download — because every one of those is somebody
 * asking the browser to do its own thing and taking it over is rude.
 */
export function interceptLinks(): void {
  document.addEventListener("click", (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const anchor = (event.target as HTMLElement | null)?.closest?.("a");
    if (!anchor) return;
    const href = anchor.getAttribute("href");
    if (!href || anchor.target || anchor.hasAttribute("download") || href.startsWith("#")) return;
    // Same origin only. An absolute address to broadpaper.com is a different
    // deployment and has to be a real navigation.
    const url = new URL(href, window.location.href);
    if (url.origin !== window.location.origin) return;
    event.preventDefault();
    go(url.pathname + url.search);
  });
}

/**
 * The path, matched against a pattern with `:name` segments.
 *
 * Returns the parameters or null. Exact-length matching, so `/data/bpd_x` cannot
 * satisfy `/data` and a typo in a link is a "not found" rather than a screen
 * rendering with an undefined id.
 */
export function matchRoute(path: string, pattern: string): Record<string, string> | null {
  const actual = path.split("?")[0]!.replace(/\/+$/, "") || "/";
  const parts = actual.split("/").filter(Boolean);
  const shape = pattern.split("/").filter(Boolean);
  if (parts.length !== shape.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < shape.length; i++) {
    const segment = shape[i]!;
    if (segment.startsWith(":")) params[segment.slice(1)] = decodeURIComponent(parts[i]!);
    else if (segment !== parts[i]) return null;
  }
  return params;
}

/** The `?name=` values, for the handful of places a screen takes one. */
export function query(path: string): URLSearchParams {
  return new URLSearchParams(path.split("?")[1] ?? "");
}
